import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ChevronDown,
  ChevronUp,
  Download,
  Mail,
  Printer,
  Send,
  X,
} from 'lucide-react'
import {
  fetchCheckRun,
  fetchFindings,
  fetchProducts,
  fetchProductVariants,
  getCheckRunReportPdfUrl,
  getEvidenceUrls,
  emailCheckRunReport,
  type FindingListDto,
} from '../services/api'
import { FilterPill, KpiCard, MimCard, MimPage, StatusPill, formatDateTime } from '../components/MimUi'

export default function Findings() {
  const [searchParams] = useSearchParams()
  const checkRunId = searchParams.get('checkRunId') || undefined
  const isSingleRun = !!checkRunId
  const [expanded, setExpanded] = useState<string | null>(null)
  const [severity, setSeverity] = useState('all')
  const [statusFilter, setStatusFilter] = useState<'Open' | 'all'>('Open')
  const [emailOpen, setEmailOpen] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [productId, setProductId] = useState('')
  const [variantId, setVariantId] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 100

  const { data: checkRun } = useQuery({
    queryKey: ['check-run', checkRunId, 'exception-report'],
    queryFn: () => fetchCheckRun(checkRunId!),
    enabled: isSingleRun,
  })
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'exception-report-filter'],
    queryFn: () => fetchProducts({ kingspanOnly: true }),
    enabled: !isSingleRun,
  })
  const { data: variants = [] } = useQuery({
    queryKey: ['product-variants', productId, 'exception-report-filter'],
    queryFn: () => fetchProductVariants(productId),
    enabled: !isSingleRun && !!productId,
  })

  const fromUtc = fromDate ? `${fromDate}T00:00:00.000Z` : undefined
  const toUtc = toDate ? `${toDate}T23:59:59.999Z` : undefined

  const { data: findingsPage, isLoading } = useQuery({
    queryKey: [
      'findings',
      checkRunId,
      isSingleRun ? 'all' : statusFilter,
      fromUtc,
      toUtc,
      productId,
      variantId,
      page,
      'exception-report',
    ],
    queryFn: () => fetchFindings({
      checkRunId,
      status: isSingleRun || statusFilter === 'all' ? undefined : statusFilter,
      fromUtc: isSingleRun ? undefined : fromUtc,
      toUtc: isSingleRun ? undefined : toUtc,
      productId: isSingleRun || !productId ? undefined : productId,
      variantId: isSingleRun || !variantId ? undefined : variantId,
      page: isSingleRun ? 1 : page,
      pageSize: isSingleRun ? 500 : pageSize,
    }),
  })

  const findings = findingsPage?.items ?? []
  const totalCount = findingsPage?.totalCount ?? findings.length
  const totalPages = findingsPage?.totalPages ?? 1

  const filtered = useMemo(() => {
    if (severity === 'all') return findings
    if (severity === 'warning') return findings.filter((f) => ['Medium', 'Low', 'Warning'].includes(f.severity))
    if (severity === 'critical') return findings.filter((f) => ['Critical', 'High'].includes(f.severity))
    return findings
  }, [findings, severity])

  const counts = useMemo(() => {
    const critical = findings.filter((f) => ['Critical', 'High'].includes(f.severity)).length
    const warnings = findings.filter((f) => ['Medium', 'Low', 'Warning'].includes(f.severity)).length
    const affectedProducts = new Set(findings.map((f) => f.productCode || f.productName).filter(Boolean)).size
    const totalChecks = checkRun?.processedCount ?? 0
    return {
      total: totalCount,
      critical,
      warnings,
      affectedProducts,
      totalChecks,
      passed: Math.max(0, totalChecks - findings.length),
    }
  }, [findings, checkRun, totalCount])

  return (
    <MimPage
      title="Exception Report"
      subtitle={isSingleRun ? 'Review identified mismatches and compliance failures for this run' : 'Every identified mismatch and compliance failure across all check runs to date'}
    >
      <div className="mim-stack">
        <MimCard>
          <div className="exception-header">
            <div>
              <div className="exception-tags">
                {isSingleRun && <span className="mim-mono">RPT-{checkRunId!.slice(0, 8)}</span>}
                <StatusPill status={`${counts.critical} Critical Issues`} tone="critical" />
                <StatusPill status={`${counts.warnings} Warnings`} tone="warning" />
              </div>
              <h2>{isSingleRun ? (checkRun?.rootUrl || checkRun?.reason || 'Website Compliance Report') : 'All Exceptions To Date'}</h2>
              <p>{isSingleRun ? (checkRun ? `Started ${formatDateTime(checkRun.startedUtc)}` : 'Loading run...') : `${counts.total} exception${counts.total === 1 ? '' : 's'} matching filters`}</p>
            </div>
            <div className="mim-toolbar">
              {isSingleRun ? (
                <a className="mim-btn mim-btn-secondary" href={getCheckRunReportPdfUrl(checkRunId!)} target="_blank" rel="noreferrer">
                  <Download size={14} /> Export PDF
                </a>
              ) : (
                <button
                  type="button"
                  className="mim-btn mim-btn-secondary"
                  onClick={() => window.print()}
                >
                  <Download size={14} /> Export PDF
                </button>
              )}
              <button
                type="button"
                className="mim-btn mim-btn-secondary"
                onClick={() => {
                  const header = ['Severity', 'Status', 'Product', 'Description', 'Target', 'CreatedUtc']
                  const rows = filtered.map((f) => [
                    f.severity,
                    f.status,
                    f.productCode || f.productName || '',
                    (f.description || '').replace(/"/g, '""'),
                    f.targetUrl || '',
                    f.createdUtc || '',
                  ])
                  const csv = [header, ...rows]
                    .map((r) => r.map((c) => `"${String(c ?? '')}"`).join(','))
                    .join('\n')
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `exception-report-${new Date().toISOString().slice(0, 10)}.csv`
                  a.click()
                  URL.revokeObjectURL(url)
                }}
              >
                <Download size={14} /> Export CSV
              </button>
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => window.print()}>
                <Printer size={14} /> Print
              </button>
              <button type="button" className="mim-btn mim-btn-gold" onClick={() => setEmailOpen(true)}>
                <Mail size={14} /> Email
              </button>
            </div>
          </div>
        </MimCard>

        <div className="mim-grid-4">
          <KpiCard label={isSingleRun ? 'Total Checks' : 'Total Findings'} value={isSingleRun ? counts.totalChecks : counts.total} />
          <KpiCard label="Critical" value={counts.critical} tone="red" />
          <KpiCard label="Warnings" value={counts.warnings} tone="orange" />
          {isSingleRun ? (
            <KpiCard label="Passed" value={counts.passed} tone="green" />
          ) : (
            <KpiCard label="Affected Products" value={counts.affectedProducts} tone="green" />
          )}
        </div>

        {!isSingleRun && (
          <MimCard title="Historic filters">
            <div className="mim-toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
              <label>
                <span className="mim-label">From</span>
                <input
                  type="date"
                  className="mim-input"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setPage(1) }}
                />
              </label>
              <label>
                <span className="mim-label">To</span>
                <input
                  type="date"
                  className="mim-input"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setPage(1) }}
                />
              </label>
              <label>
                <span className="mim-label">Product</span>
                <select
                  className="mim-select"
                  value={productId}
                  onChange={(e) => {
                    setProductId(e.target.value)
                    setVariantId('')
                    setPage(1)
                  }}
                >
                  <option value="">All products</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.code})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="mim-label">Variant</span>
                <select
                  className="mim-select"
                  value={variantId}
                  disabled={!productId}
                  onChange={(e) => { setVariantId(e.target.value); setPage(1) }}
                >
                  <option value="">All variants</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.variantCode || variant.id}
                    </option>
                  ))}
                </select>
              </label>
              {(fromDate || toDate || productId || variantId) && (
                <button
                  type="button"
                  className="mim-btn mim-btn-secondary"
                  onClick={() => {
                    setFromDate('')
                    setToDate('')
                    setProductId('')
                    setVariantId('')
                    setPage(1)
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          </MimCard>
        )}

        <div className="mim-toolbar">
          <span className="mim-label" style={{ margin: 0 }}>Filter:</span>
          {['all', 'critical', 'warning'].map((item) => (
            <FilterPill key={item} active={severity === item} onClick={() => setSeverity(item)}>
              {item === 'all' ? 'All Exceptions' : item}
            </FilterPill>
          ))}
          {!isSingleRun && (
            <>
              <span className="mim-label" style={{ margin: '0 0 0 12px' }}>Status:</span>
              <FilterPill active={statusFilter === 'Open'} onClick={() => { setStatusFilter('Open'); setPage(1) }}>Open only</FilterPill>
              <FilterPill active={statusFilter === 'all'} onClick={() => { setStatusFilter('all'); setPage(1) }}>Include resolved</FilterPill>
            </>
          )}
          <span className="history-count">
            Showing {filtered.length} of {totalCount} exception{totalCount === 1 ? '' : 's'}
            {!isSingleRun && totalPages > 1 ? ` · page ${page}/${totalPages}` : ''}
          </span>
        </div>

        <MimCard>
          <div className="mim-table-wrap">
            <table className="mim-table">
              <thead>
                <tr>
                  <th>Ref</th>
                  <th>Product</th>
                  <th>Variant</th>
                  <th>Rule</th>
                  <th>Check type</th>
                  <th>Expected (PCR)</th>
                  <th>Found on website</th>
                  <th>Evidence</th>
                  <th>Severity</th>
                  <th>Clause</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={11}>Loading findings...</td></tr>}
                {!isLoading && filtered.length === 0 && <tr><td colSpan={11}>No exceptions match the selected filter.</td></tr>}
                {filtered.map((finding, index) => (
                  <Fragment key={finding.id}>
                    <tr onClick={() => setExpanded((current) => current === finding.id ? null : finding.id)}>
                      <td className="mono">EX-{String((page - 1) * pageSize + index + 1).padStart(3, '0')}</td>
                      <td>{finding.productName || finding.productCode || '-'}</td>
                      <td>{finding.variantCode || (finding.productVariantId ? finding.productVariantId.slice(0, 8) : '-')}</td>
                      <td>{ruleFromFinding(finding)}</td>
                      <td><StatusPill status={checkTypeFromFinding(finding)} tone="info" /></td>
                      <td className="mono">{finding.expectedValue || '-'}</td>
                      <td className="mono exception-observed">{finding.observedValue || '-'}</td>
                      <td>
                        {finding.evidenceQuote
                          ? <span title={finding.evidenceQuote}>{truncate(finding.evidenceQuote, 60)}</span>
                          : <span className="mim-help-text">{finding.evidenceReadiness || 'None'}</span>}
                      </td>
                      <td><StatusPill status={friendlySeverity(finding.severity)} /></td>
                      <td>{finding.mimClauseMappings || '-'}</td>
                      <td>{expanded === finding.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</td>
                    </tr>
                    {expanded === finding.id && (
                      <tr>
                        <td colSpan={11} className="exception-expanded">
                          <div className="mim-grid-3">
                            <div>
                              <strong>PCR Source of Truth</strong>
                              <p className="mim-mono">{finding.expectedValue || 'Not available'}</p>
                            </div>
                            <div>
                              <strong>Found on Website</strong>
                              <p className="mim-mono exception-observed">{finding.observedValue || 'Not available'}</p>
                            </div>
                            <div>
                              <strong>Recommendation</strong>
                              <p>{recommendationFor(finding)}</p>
                            </div>
                          </div>
                          <div className="mim-grid-3" style={{ marginTop: 8 }}>
                            <div>
                              <strong>Rule / Field</strong>
                              <p className="mim-mono">{finding.ruleKey || finding.fieldKey || finding.findingType}</p>
                            </div>
                            <div>
                              <strong>Check date</strong>
                              <p>{formatDateTime(finding.createdUtc)}</p>
                            </div>
                            <div>
                              <strong>Evidence artefacts</strong>
                              <p>
                                {finding.hasScreenshotEvidence && <a href={getEvidenceUrls(finding.checkRunId, finding.targetId).screenshot} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>Screenshot</a>}
                                {finding.hasExtractedTextEvidence && <a href={getEvidenceUrls(finding.checkRunId, finding.targetId).text} target="_blank" rel="noreferrer" style={{ marginRight: 10 }}>Extracted text</a>}
                                {finding.hasLinksEvidence && <a href={getEvidenceUrls(finding.checkRunId, finding.targetId).links} target="_blank" rel="noreferrer">Links</a>}
                                {!finding.hasScreenshotEvidence && !finding.hasExtractedTextEvidence && !finding.hasLinksEvidence && 'No stored artefacts'}
                              </p>
                            </div>
                          </div>
                          {finding.evidenceQuote && (
                            <p className="exception-context"><strong>Quote:</strong> “{finding.evidenceQuote}”</p>
                          )}
                          {finding.context && <p className="exception-context">{finding.context}</p>}
                          {finding.targetUrl && <a href={finding.targetUrl} target="_blank" rel="noreferrer">{finding.targetUrl}</a>}
                          {!isSingleRun && <div><Link to={`/check-runs/${finding.checkRunId}`}>View run &rarr;</Link></div>}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {!isSingleRun && totalPages > 1 && (
            <div className="mim-toolbar" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button type="button" className="mim-btn mim-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </button>
              <span className="mim-help-text">Page {page} of {totalPages}</span>
              <button type="button" className="mim-btn mim-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next
              </button>
            </div>
          )}
        </MimCard>
      </div>

      {emailOpen && (
        <EmailReportModal
          checkRunId={checkRunId}
          reportId={checkRunId ? `RPT-${checkRunId.slice(0, 8)}` : 'Filtered exceptions'}
          critical={counts.critical}
          warnings={counts.warnings}
          total={counts.total}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </MimPage>
  )
}

function EmailReportModal({
  checkRunId,
  reportId,
  critical,
  warnings,
  total,
  onClose,
}: {
  checkRunId?: string
  reportId: string
  critical: number
  warnings: number
  total: number
  onClose: () => void
}) {
  const [emailTo, setEmailTo] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <div className="mim-modal-backdrop">
      <div className="mim-modal">
        <div className="mim-modal-header">
          <h3>Email Exception Report</h3>
          <button type="button" className="mim-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="mim-stack" style={{ padding: 16 }}>
          <p className="mim-help-text">
            {checkRunId
              ? `Send ${reportId} (${critical} critical, ${warnings} warnings) with the DOCX spot-check report attached.`
              : `Email a summary of ${total} filtered exceptions (${critical} critical, ${warnings} warnings).`}
          </p>
          <label>
            <span className="mim-label">Recipient email</span>
            <input className="mim-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="owner@example.com" />
          </label>
          {error && <div className="mim-note" style={{ color: '#991b1b' }}>{error}</div>}
          {sent ? (
            <div className="mim-note">Report emailed to {emailTo}.</div>
          ) : (
            <button
              type="button"
              className="mim-btn mim-btn-primary"
              disabled={!emailTo.trim() || sending}
              onClick={async () => {
                setSending(true)
                setError(null)
                try {
                  if (checkRunId) {
                    await emailCheckRunReport(checkRunId, emailTo.trim())
                  } else {
                    const subject = encodeURIComponent(`Exception report — ${total} findings`)
                    const body = encodeURIComponent(
                      `Exception report summary\n\nTotal: ${total}\nCritical: ${critical}\nWarnings: ${warnings}\n\nOpen the Exception Report page in MIM GRC for full details.`,
                    )
                    window.location.href = `mailto:${encodeURIComponent(emailTo.trim())}?subject=${subject}&body=${body}`
                  }
                  setSent(true)
                } catch (err: any) {
                  setError(err?.response?.data?.message || err?.message || 'Failed to send email.')
                } finally {
                  setSending(false)
                }
              }}
            >
              <Send size={14} /> {sending ? 'Sending…' : 'Send report'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function ruleFromFinding(finding: FindingListDto) {
  if (finding.ruleKey) return friendlyRuleKey(finding.ruleKey)
  if (finding.fieldKey) return finding.fieldKey
  return finding.findingType || 'Finding'
}

function friendlyRuleKey(key: string) {
  return key
    .replace(/^(claim:|rule:|marketing_|restricted_term_)/i, '')
    .replace(/[_:]+/g, ' ')
    .trim()
}

function checkTypeFromFinding(finding: FindingListDto) {
  if (finding.ruleType) {
    if (/restricted/i.test(finding.ruleType)) return 'Banned term'
    if (/unsupported|marketing/i.test(finding.ruleType)) return 'Claim check'
    if (/required/i.test(finding.ruleType)) return 'Required reference'
    if (/brokenlink/i.test(finding.ruleType)) return 'Link check'
    return finding.ruleType
  }
  if (finding.matchMode) return `${finding.matchMode} match`
  if (/missing|incomplete/i.test(finding.findingType || '')) return 'Missing field'
  if (/mismatch/i.test(finding.findingType || '')) return 'Field comparison'
  return 'Rule check'
}

function truncate(value: string, max: number) {
  return value.length > max ? `${value.slice(0, max)}…` : value
}

function friendlySeverity(severity: string) {
  if (['Critical', 'High'].includes(severity)) return 'Critical'
  if (['Medium', 'Low', 'Warning'].includes(severity)) return 'Warning'
  return severity
}

function recommendationFor(finding: FindingListDto) {
  if (finding.findingType?.toLowerCase().includes('missing')) {
    return 'Add the missing PCR value to the website content and re-run the check.'
  }
  if (finding.findingType?.toLowerCase().includes('restricted') || finding.findingType?.toLowerCase().includes('claim')) {
    return 'Remove or reword the disallowed claim, then re-run the check.'
  }
  return 'Align the website value with the approved PCR and re-run the check.'
}
